/**
 * KPI domain model.
 *
 * Two rules from the platform carry over unchanged:
 *
 *  1. A KPI that cannot be computed is shown as NOT COMPUTABLE, with the
 *     reason — never as zero. A zero there is a lie that reads as good news.
 *  2. Targets are shown, not hidden, so nobody is graded against a number they
 *     have never seen. A measure whose pack declares no target is shown with
 *     its value and left ungraded — never graded against an invented zero,
 *     which fails every reading above nothing and dragged the overall grade to F.
 */
import { locale } from "../i18n";

export type KpiDirection = "up" | "down";
export type KpiGrade = "A" | "B" | "C" | "D" | "F";
export type ReadinessState = "go" | "caution" | "nogo" | "idle";

export interface Kpi {
  key: string;
  label: string;
  unit: string;
  /** Which way is better. */
  direction: KpiDirection;
  /** null when no target is declared: the value is shown, and not graded. A declared 0 is a real target. */
  target: number | null;
  /**
   * Where the target came from: the pack's declaration, or the operator's own
   * — proposed (a benchmark to start from) or agreed (signed off). A grade
   * against a proposed target is shown as such, never as a settled judgement.
   */
  targetStatus?: "pack" | "proposed" | "agreed";
  /** What the operator's target was taken from. */
  targetBasis?: string;
  /** Who set the operator's target, and when (absent for a pack target). */
  targetSetBy?: string;
  targetSetAt?: string;
  /** The operator's row is this site's own, or tenant-wide. Only a site's own can be retired from it. */
  targetScope?: "site" | "tenant";
  /** The pack's declaration, shown beside the operator's own; null when the pack declares none. */
  packTarget?: number | null;
  /** null when not computable; see `reason`. */
  value: number | null;
  /** Why the value is null, when it is. */
  reason?: string;
  /** Where the number came from. */
  source?: string;
  /** Optional recent history, oldest first, for a sparkline. */
  history?: number[];
}

export interface GradedKpi extends Kpi {
  ratio: number | null;
  grade: KpiGrade | null;
  state: ReadinessState;
}

export function gradeFromRatio(ratio: number): KpiGrade {
  if (ratio >= 1) return "A";
  if (ratio >= 0.95) return "B";
  if (ratio >= 0.85) return "C";
  if (ratio >= 0.75) return "D";
  return "F";
}

/**
 * Ratio of achievement against target, 1.0 = on target.
 * Carried over from the Madinah application's `execKpis` so grades agree
 * between the two front ends during the migration.
 */
export function achievementRatio(kpi: Kpi): number | null {
  const v = kpi.value;
  if (v == null || kpi.target == null) return null;
  if (kpi.direction === "up") {
    return kpi.target ? v / kpi.target : 1;
  }
  if (kpi.target === 0) {
    return v === 0 ? 1 : Math.max(0, 1 - v * 0.34);
  }
  return Math.max(0, Math.min(1.3, 1 + (kpi.target - v) / Math.max(1, kpi.target)));
}

export function stateFromRatio(ratio: number | null): ReadinessState {
  if (ratio == null) return "idle";
  if (ratio >= 0.95) return "go";
  if (ratio >= 0.85) return "caution";
  return "nogo";
}

export function gradeKpi(kpi: Kpi): GradedKpi {
  const ratio = achievementRatio(kpi);
  return {
    ...kpi,
    ratio,
    grade: ratio == null ? null : gradeFromRatio(ratio),
    state: stateFromRatio(ratio),
  };
}

export interface OverallGrade {
  pct: number | null;
  grade: KpiGrade | null;
  scored: number;
  notComputable: number;
  /** Computed, but with no declared target to grade against. */
  noTarget: number;
  /** Graded, against a target that is proposed rather than agreed. */
  proposed: number;
}

export function overallGrade(kpis: readonly GradedKpi[]): OverallGrade {
  const scored = kpis.filter((k) => k.ratio != null);
  const notComputable = kpis.filter((k) => k.value == null).length;
  const noTarget = kpis.filter((k) => k.value != null && k.target == null).length;
  const proposed = scored.filter((k) => k.targetStatus === "proposed").length;
  if (!scored.length) return { pct: null, grade: null, scored: 0, notComputable, noTarget, proposed };
  const pct = scored.reduce((t, k) => t + Math.min(1.1, k.ratio as number), 0) / scored.length;
  return { pct, grade: gradeFromRatio(pct), scored: scored.length, notComputable, noTarget, proposed };
}

/*
 * Grouped thousands, as the platform writes money: "360000 kWh" is misread at
 * a glance on exactly the tiles a board reads. The reader's locale, which keeps
 * Western digits in Arabic too; English otherwise, whatever the browser says,
 * because an ar-SA browser would mix Arabic-Indic digits into a Latin tile.
 */
const numLocale = () => locale() ?? "en-US";

export function formatKpiValue(kpi: Kpi): string {
  if (kpi.value == null) return "—";
  const v = kpi.value;
  // A small reading rounded to one decimal prints "0.0", a zero it is not.
  if (v !== 0 && Math.abs(v) < 0.05) return v.toLocaleString(numLocale(), { maximumSignificantDigits: 2 });
  const digits = Math.abs(v) >= 100 || Number.isInteger(v) ? 0 : 1;
  return v.toLocaleString(numLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** A target is a declared figure, not a reading: grouped, but never rounded. */
export function formatKpiTarget(kpi: Kpi): string {
  if (kpi.target == null) return "—";
  return kpi.target.toLocaleString(numLocale(), { maximumFractionDigits: 6 });
}

/**
 * The names the executive desks use for a few KPIs, mapped to the platform
 * metric that measures the same thing. The desks were written against the
 * sample's names ("queue", "security", "mttr_p1_hours"); the live packs name
 * them differently, so on the platform every such lookup found nothing and
 * the tile read "no KPI" (audit, 2026-10-10). Only true equivalents are here;
 * where the platform measures something close but not identical, the label
 * says which figure stands in. Names with no platform metric (pax against
 * plan, bags on time, equipment out, punctuality, weather) stay unmatched and
 * the desks say so.
 */
export const KPI_ALIAS: Record<string, { key: string; note?: string }> = {
  security: { key: "open_security_incidents" },
  queue: { key: "screening_mean_queue_minutes", note: "mean screening queue, not the worst queue" },
  mttr_p1_hours: { key: "mttr_hours", note: "all priorities, not P1 alone" },
};

/** A KPI by the desk's name, falling back to the platform metric that stands in for it. */
export function findKpi<K extends Kpi>(kpis: readonly K[], key: string): K | undefined {
  const own = kpis.find((k) => k.key === key);
  if (own) return own;
  const alias = KPI_ALIAS[key];
  if (!alias) return undefined;
  const hit = kpis.find((k) => k.key === alias.key);
  return hit && alias.note ? { ...hit, label: `${hit.label} (${alias.note})` } : hit;
}
